import { z } from 'zod';
import { messageKey } from './messages.js';
import { archiveProviderSchema, codecHintSchema } from './cameras.js';
/**
 * Архив видео (§8.2, §11 фаза 8).
 *
 * Единого работающего стандарта архива нет — ТЗ говорит об этом прямо, —
 * поэтому здесь описано не «как устроен архив вообще», а ровно то, чем
 * платформа обменивается с браузером: что камера умеет, где на шкале есть
 * запись, и как получить кусок этой записи в виде обычного потока.
 */
/**
 * Сколько живёт кэш таймлайна (§11, фаза 8, задача 4).
 *
 * Шестьдесят секунд заданы ТЗ. Обращение к камере за списком записей —
 * дорогая операция: Hikvision отвечает на поиск секундами, Dahua требует
 * четырёх запросов подряд, и оба делают это по тому же каналу, по которому
 * идёт видео.
 */
export const ARCHIVE_TIMELINE_CACHE_SEC = 60;
/**
 * Предел ширины запрашиваемого окна таймлайна.
 *
 * Неделя, а не месяц: за месяц камера вернёт десятки тысяч отрезков, которые
 * всё равно не поместятся в шкалу шириной в экран. Интерфейс показывает сутки,
 * запас — на переход между часовыми поясами и на выделение через полночь.
 */
export const ARCHIVE_TIMELINE_MAX_HOURS = 24 * 7;
/**
 * Предел длины экспортируемого фрагмента.
 *
 * Полчаса — это уже сотни мегабайт, которые идут через туннель площадки
 * и делят его с живым видео. Больше отдаёт не платформа, а сам регистратор
 * своими средствами.
 */
export const ARCHIVE_EXPORT_MAX_MINUTES = 30;
/**
 * Скорости воспроизведения, предлагаемые интерфейсом.
 *
 * Список конечный намеренно: произвольная скорость означала бы поле ввода
 * там, где нужны две кнопки, а камера всё равно принимает не всякое число.
 */
export const ARCHIVE_SPEEDS = [0.5, 1, 2, 4];
export const archiveSpeedSchema = z
    .number()
    .refine((value) => ARCHIVE_SPEEDS.includes(value), messageKey('validation.archive.speed', { speeds: ARCHIVE_SPEEDS.join(', ') }));
/**
 * Отрезок с записью на шкале времени.
 *
 * Границы — моменты, а не длительность: камера отдаёт именно их, а считать
 * длительность приходится по-разному в разных провайдерах.
 */
export const archiveSegmentSchema = z.object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
});
/**
 * Что умеет архив конкретной камеры (§11, фаза 8, задача 7).
 *
 * Отдельным запросом, а не полем камеры: провайдер задан человеком заранее,
 * а вот отвечает ли камера — выясняется только обращением к ней, и ответ
 * этот живёт минуты, а не до перезагрузки.
 *
 * `available: false` обязано приходить с `reason`: интерфейс говорит, что
 * архив недоступен, **и почему**, — это прямое требование задачи 7.
 */
export const archiveCapabilitiesSchema = z.object({
    provider: archiveProviderSchema.nullable(),
    /** Дотянулись ли до архива прямо сейчас. */
    available: z.boolean(),
    /** Почему недоступен, человеческим языком. `null` — доступен. */
    reason: z.string().nullable(),
    /** Умеет ли провайдер сказать, где именно есть запись. */
    supportsTimeline: z.boolean(),
    /** Умеет ли начинать воспроизведение с произвольного момента. */
    supportsSeek: z.boolean(),
    /** Умеет ли камера отдавать запись быстрее или медленнее реального времени. */
    supportsSpeed: z.boolean(),
    /** Умеет ли провайдер отдать фрагмент файлом. */
    supportsExport: z.boolean(),
    /** Через какого агента шли к камере. `null` — напрямую. */
    viaAgent: z.string().nullable(),
});
/**
 * Запрошенный интервал.
 *
 * Проверка «начало раньше конца» и предел ширины стоят здесь, на границе:
 * перевёрнутый интервал камера воспримет по-своему — одна вернёт пустоту,
 * другая всю запись целиком, — и разбираться в этом потом будет некому.
 */
export const archiveRangeSchema = z
    .object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
})
    .refine((value) => Date.parse(value.from) < Date.parse(value.to), {
    message: messageKey('validation.range.startBeforeEnd'),
    path: ['to'],
});
export const archiveTimelineQuerySchema = archiveRangeSchema.refine((value) => Date.parse(value.to) - Date.parse(value.from) <= ARCHIVE_TIMELINE_MAX_HOURS * 3600 * 1000, {
    message: messageKey('validation.archive.rangeTooWide', {
        days: ARCHIVE_TIMELINE_MAX_HOURS / 24,
    }),
    path: ['to'],
});
/**
 * Таймлайн: где на шкале есть запись (§11, фаза 8, задача 6).
 *
 * `capabilities` едет вместе с отрезками, а не отдельным запросом: шкала
 * и подпись под ней рисуются одновременно, и второй запрос ради подписи —
 * лишний поход к камере.
 */
export const archiveTimelineSchema = z.object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    segments: z.array(archiveSegmentSchema),
    capabilities: archiveCapabilitiesSchema,
    /** Ответ взят из кэша (§11, фаза 8, задача 4) — видно в интерфейсе. */
    cached: z.boolean(),
    /** Ответ получен, но неполон: например, камера обрезала список. */
    warning: z.string().nullable(),
});
/** Тело запроса на архивный поток (§8.2). */
export const createArchiveStreamSchema = z
    .object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    speed: archiveSpeedSchema.default(1),
})
    .refine((value) => Date.parse(value.from) < Date.parse(value.to), {
    message: messageKey('validation.range.startBeforeEnd'),
    path: ['to'],
});
/**
 * Выданный доступ к архивному потоку.
 *
 * Поля намеренно те же, что и у живого потока: доставка в браузер общая —
 * тот же WHEP, тот же резервный канал, тот же одноразовый тикет. Отличается
 * только источник, который поднимается в go2rtc, и это разница внутри `api`,
 * а не в браузере.
 */
export const archiveStreamSchema = z.object({
    whepUrl: z.string(),
    mseUrl: z.string(),
    ticket: z.string(),
    expiresAt: z.iso.datetime(),
    leaseId: z.uuid(),
    /** Сессия §7.8 вида `video_archive` — под неё пишется аудит. */
    sessionId: z.uuid(),
    /** Что открыли на самом деле: интервал мог быть подрезан по границам записи. */
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    /** Скорость, которую приняла камера. `1`, если она её не умеет. */
    speed: z.number(),
    /** Заполнено, когда выданное разошлось с запрошенным (§12 запрещает молчать). */
    warning: z.string().nullable(),
    codecHint: codecHintSchema.nullable(),
});
/**
 * Настройки архива, которые администратор задаёт руками (`cameras.archive_config`).
 *
 * Свободный `Record` в `cameras.archiveConfig` остаётся: провайдеры вправе
 * хранить там своё. Здесь описано то, что читают наши три реализации, —
 * и описано схемой, потому что значения приходят из БД, а §6.1 требует
 * проверять всё, что пришло не из нашего кода.
 *
 * Все поля необязательны: камера, заведённая без единой настройки, обязана
 * работать на умолчаниях — иначе провайдер превращается в анкету.
 */
export const archiveSettingsSchema = z.object({
    /** Порт HTTP-интерфейса камеры: ISAPI и CGI живут на нём, а не на RTSP. */
    httpPort: z.coerce.number().int().min(1).max(65535).default(80),
    /** `https` для камер, у которых HTTP выключён. */
    scheme: z.enum(['http', 'https']).default('http'),
    /** Канал регистратора. Единица — сама камера; у NVR каналов много. */
    channel: z.coerce.number().int().min(1).max(256).default(1),
    /**
     * Дорожка Hikvision: `101` — основной поток первого канала, `102` —
     * substream. Архив обычно пишется в основной.
     */
    track: z.coerce.number().int().min(1).max(9999).default(101),
    /**
     * Сдвиг часов камеры относительно UTC, в минутах.
     *
     * Dahua принимает и возвращает **местное** время камеры без указания
     * пояса, и без этого сдвига запись за десять утра ищется в час дня.
     * Ноль означает «камера живёт по UTC» — так её и стоит настраивать.
     */
    tzOffsetMinutes: z.coerce.number().int().min(-840).max(840).default(0),
    /**
     * Имя параметра скорости в RTSP-URL.
     *
     * Пусто — камера скорости в адресе не принимает, и `supportsSpeed`
     * у провайдера будет `false`. Заголовок `Speed:` протокола RTSP платформе
     * недоступен: поток поднимает go2rtc, а своего RTSP-кода платформа
     * не пишет вовсе (§3.2). Подробности — ADR 017.
     */
    speedParam: z.string().trim().max(64).optional(),
    /**
     * Шаблон архивного RTSP-URL для провайдера `rtsp_replay`.
     *
     * Подстановки: `{from}`, `{to}` — моменты в формате `timeFormat`,
     * `{channel}`, `{track}`, `{path}` — путь живого потока камеры.
     * Пример: `/cam/playback?channel={channel}&starttime={from}&endtime={to}`.
     */
    urlTemplate: z.string().trim().max(512).optional(),
    /**
     * Как записывать моменты в шаблоне:
     * `iso-basic` — `20260823T100000Z` (Hikvision и большинство ONVIF),
     * `dahua` — `2026_08_23_10_00_00` в местном времени камеры,
     * `unix` — секунды эпохи.
     */
    timeFormat: z.enum(['iso-basic', 'dahua', 'unix']).default('iso-basic'),
});
/**
 * Разбор настроек архива с подстановкой умолчаний.
 *
 * Мусор в конфигурации не должен ронять просмотр: администратор мог вписать
 * туда что угодно, а камера от этого не перестала снимать. Непонятое
 * заменяется умолчанием, и это осознанно мягче общего правила §6.1 —
 * граница здесь наша собственная БД, а не чужая сеть.
 */
export function readArchiveSettings(raw) {
    const parsed = archiveSettingsSchema.safeParse(raw ?? {});
    return parsed.success ? parsed.data : archiveSettingsSchema.parse({});
}
/**
 * Имя архивного потока в go2rtc (§11, фаза 8, задача 5).
 *
 * В имени есть и интервал, и скорость: два инженера, открывшие один и тот же
 * фрагмент, обязаны попасть в один поток — иначе камера отдаёт одно и то же
 * дважды, — а разные фрагменты обязаны в один поток не попасть.
 *
 * Момент округляется до секунды: миллисекунды в имени означали бы новый
 * поток на каждое движение по шкале.
 */
export function archiveStreamName(base, from, to, speed = 1) {
    const stamp = (value) => Math.floor(Date.parse(value) / 1000)
        .toString(36)
        .padStart(7, '0');
    return `${base}_arch_${stamp(from)}_${stamp(to)}_x${String(speed).replace('.', 'p')}`;
}
/** Архивный ли это поток. Нужно гашению и журналу: живой и архивный ведут себя по-разному. */
export function isArchiveStreamName(name) {
    return name.includes('_arch_');
}
/** Ключ кэша таймлайна в Redis (задача 4). */
export function archiveTimelineKey(cameraId, from, to) {
    return `video:timeline:${cameraId}:${Date.parse(from)}:${Date.parse(to)}`;
}
/**
 * Склейка отрезков записи.
 *
 * Камеры отдают запись кусками по несколько минут и часто с секундными
 * зазорами между ними — на шкале шириной в сутки это выглядело бы как
 * пунктир там, где запись непрерывна. Отрезки ближе двух секунд считаются
 * одним, пересекающиеся сливаются.
 *
 * Заодно сортировка: Dahua отдаёт файлы в порядке нахождения, а не времени.
 */
export function mergeSegments(segments, gapToleranceMs = 2000) {
    const ordered = [...segments]
        .filter((segment) => Date.parse(segment.from) < Date.parse(segment.to))
        .sort((a, b) => Date.parse(a.from) - Date.parse(b.from));
    const merged = [];
    for (const segment of ordered) {
        const last = merged[merged.length - 1];
        if (last !== undefined && Date.parse(segment.from) - Date.parse(last.to) <= gapToleranceMs) {
            if (Date.parse(segment.to) > Date.parse(last.to)) {
                last.to = segment.to;
            }
            continue;
        }
        merged.push({ from: segment.from, to: segment.to });
    }
    return merged;
}
/** Сколько всего записано в отрезках, в секундах, — подпись под шкалой. */
export function recordedSeconds(segments) {
    return Math.round(mergeSegments(segments).reduce((total, segment) => total + (Date.parse(segment.to) - Date.parse(segment.from)), 0) / 1000);
}
/** Попадает ли момент в записанное. По этому признаку шкала не даёт ткнуть в пустоту. */
export function hasRecordingAt(segments, at) {
    return segments.some((segment) => Date.parse(segment.from) <= at && at < Date.parse(segment.to));
}
/**
 * Ближайший записанный момент не раньше указанного.
 *
 * Щелчок по пустому месту шкалы не должен открывать пустоту: инженер целился
 * в «примерно сюда», а не в конкретную миллисекунду. `null` — дальше по шкале
 * записи нет вовсе.
 */
export function nextRecordedMoment(segments, at) {
    if (hasRecordingAt(segments, at)) {
        return at;
    }
    const starts = mergeSegments(segments)
        .map((segment) => Date.parse(segment.from))
        .filter((start) => start >= at)
        .sort((a, b) => a - b);
    return starts[0] ?? null;
}
/**
 * Все провайдеры — ключами словаря, для выпадающего списка.
 *
 * Два имени из трёх — собственные («Hikvision ISAPI», «Dahua CGI») и в обоих
 * языках одинаковы, но третье («RTSP-повтор») переводится, и держать список
 * наполовину ключами, наполовину подписями значило бы однажды не заметить,
 * что четвёртый провайдер добавили не туда.
 */
export const ARCHIVE_PROVIDER_LABEL_KEYS = {
    hikvision_isapi: 'archive.provider.hikvisionIsapi',
    dahua_cgi: 'archive.provider.dahuaCgi',
    rtsp_replay: 'archive.provider.rtspReplay',
};
//# sourceMappingURL=archive.js.map